/** SEO tool directory data rendered on /seo-tools. */
export type Tool = {
  slug: string;
  name: string;
  category: ToolCategory;
  tagline: string;
  rating: number; // 0-5
  pricing: "Free" | "Freemium" | "Paid";
  startsAt?: string; // e.g. "$99/mo"
  pros: string[];
  cons: string[];
  bestFor: string;
  affiliate: string; // outbound URL (affiliate or referral)
  badge?: "Editor's pick" | "Best value" | "Free favorite";
};

export type ToolCategory =
  | "All-in-one SEO"
  | "Keyword Research"
  | "Technical SEO"
  | "Rank Tracking"
  | "Content & AI"
  | "Link Building"
  | "Local SEO"
  | "Analytics"
  | "AEO / GEO";

export const TOOL_CATEGORIES: ToolCategory[] = [
  "All-in-one SEO",
  "Keyword Research",
  "Technical SEO",
  "Rank Tracking",
  "Content & AI",
  "Link Building",
  "Local SEO",
  "Analytics",
  "AEO / GEO",
];

export const TOOLS: Tool[] = [
  {
    slug: "ahrefs",
    name: "Ahrefs",
    category: "All-in-one SEO",
    tagline: "The deepest backlink index on the market and a rock-solid keyword explorer.",
    rating: 4.9,
    pricing: "Paid",
    startsAt: "$129/mo",
    pros: ["Unmatched backlink data", "Best-in-class Site Audit", "Keyword Explorer with parent topic"],
    cons: ["Expensive for solos", "Credit limits on Lite plan"],
    bestFor: "Agencies and serious in-house SEOs.",
    affiliate: "https://ahrefs.com/?ref=freelancertamal",
    badge: "Editor's pick",
  },
  {
    slug: "semrush",
    name: "Semrush",
    category: "All-in-one SEO",
    tagline: "The Swiss army knife — SEO, PPC, content, and competitive intel in one suite.",
    rating: 4.7,
    pricing: "Paid",
    startsAt: "$139.95/mo",
    pros: ["Massive keyword database", "Strong PPC + competitor research", "Position tracking included"],
    cons: ["UI can feel bloated", "Backlink index slightly behind Ahrefs"],
    bestFor: "Marketers who need SEO + paid in one tool.",
    affiliate: "https://semrush.com/?ref=freelancertamal",
  },
  {
    slug: "moz-pro",
    name: "Moz Pro",
    category: "All-in-one SEO",
    tagline: "Beginner-friendly SEO suite with the original DA score baked in.",
    rating: 4.2,
    pricing: "Paid",
    startsAt: "$99/mo",
    pros: ["Easy to learn", "Solid local SEO add-ons", "Great Q&A community"],
    cons: ["Smaller index than Ahrefs/Semrush", "Slower crawler"],
    bestFor: "Small business owners learning SEO.",
    affiliate: "https://moz.com/?ref=freelancertamal",
  },
  {
    slug: "google-search-console",
    name: "Google Search Console",
    category: "Analytics",
    tagline: "Source-of-truth data straight from Google. Non-negotiable for every site.",
    rating: 5.0,
    pricing: "Free",
    pros: ["First-party Google data", "Coverage + Core Web Vitals reports", "URL inspection tool"],
    cons: ["Only 16 months of history", "Sampling on big sites"],
    bestFor: "Every site, every project, every audit.",
    affiliate: "https://search.google.com/search-console",
    badge: "Free favorite",
  },
  {
    slug: "google-analytics-4",
    name: "Google Analytics 4",
    category: "Analytics",
    tagline: "Free, event-based analytics with deep funnel + audience reporting.",
    rating: 4.0,
    pricing: "Free",
    pros: ["Free", "Event-based model", "BigQuery export"],
    cons: ["Steep learning curve", "Sampling on free tier"],
    bestFor: "Conversion + attribution tracking.",
    affiliate: "https://analytics.google.com",
  },
  {
    slug: "screaming-frog",
    name: "Screaming Frog SEO Spider",
    category: "Technical SEO",
    tagline: "The desktop crawler I open on every single technical audit.",
    rating: 4.9,
    pricing: "Freemium",
    startsAt: "£199/yr",
    pros: ["Crawls anything", "JS rendering", "Custom extraction with XPath"],
    cons: ["Desktop only", "RAM-hungry on big sites"],
    bestFor: "Technical SEO audits and migrations.",
    affiliate: "https://www.screamingfrog.co.uk/seo-spider/",
    badge: "Editor's pick",
  },
  {
    slug: "sitebulb",
    name: "Sitebulb",
    category: "Technical SEO",
    tagline: "Visual-first crawler with the best audit reports for client deliverables.",
    rating: 4.6,
    pricing: "Paid",
    startsAt: "$13.50/mo",
    pros: ["Beautiful audit reports", "Hint system explains issues", "Cloud + desktop options"],
    cons: ["Pricier than Frog at scale"],
    bestFor: "Consultants who deliver audits to clients.",
    affiliate: "https://sitebulb.com/?ref=freelancertamal",
  },
  {
    slug: "pagespeed-insights",
    name: "PageSpeed Insights",
    category: "Technical SEO",
    tagline: "Free Core Web Vitals data straight from CrUX + Lighthouse.",
    rating: 4.5,
    pricing: "Free",
    pros: ["Real user CrUX data", "Lab + field metrics", "Actionable recommendations"],
    cons: ["No historical tracking"],
    bestFor: "Quick LCP / INP / CLS checks.",
    affiliate: "https://pagespeed.web.dev",
  },
  {
    slug: "gtmetrix",
    name: "GTmetrix",
    category: "Technical SEO",
    tagline: "Pretty waterfall charts and historical performance tracking.",
    rating: 4.2,
    pricing: "Freemium",
    startsAt: "$10/mo",
    pros: ["Waterfalls", "Multi-region testing", "Historical charts"],
    cons: ["Free tier limited"],
    bestFor: "Page speed monitoring across regions.",
    affiliate: "https://gtmetrix.com",
  },
  {
    slug: "ahrefs-webmaster-tools",
    name: "Ahrefs Webmaster Tools",
    category: "Technical SEO",
    tagline: "Free Site Audit + backlink data for verified domains.",
    rating: 4.7,
    pricing: "Free",
    pros: ["Free Site Audit", "Backlink profile of your own site", "No credit card"],
    cons: ["Only your verified properties"],
    bestFor: "Owners who can't justify full Ahrefs yet.",
    affiliate: "https://ahrefs.com/webmaster-tools",
    badge: "Free favorite",
  },
  {
    slug: "keywords-everywhere",
    name: "Keywords Everywhere",
    category: "Keyword Research",
    tagline: "Inline keyword volumes on Google, YouTube, Amazon — for pennies.",
    rating: 4.4,
    pricing: "Paid",
    startsAt: "$1.25/mo",
    pros: ["Cheap credits", "Works inside Google", "Trends + related"],
    cons: ["Volume accuracy varies"],
    bestFor: "Solo SEOs and content marketers.",
    affiliate: "https://keywordseverywhere.com",
    badge: "Best value",
  },
  {
    slug: "lowfruits",
    name: "LowFruits",
    category: "Keyword Research",
    tagline: "Find weak SERPs and forum-dominated keywords you can actually rank for.",
    rating: 4.5,
    pricing: "Freemium",
    startsAt: "$0 / pay-as-you-go",
    pros: ["Weak-spot SERP detection", "Affordable credits", "Cluster view"],
    cons: ["Smaller dataset"],
    bestFor: "Niche site builders.",
    affiliate: "https://lowfruits.io",
  },
  {
    slug: "google-keyword-planner",
    name: "Google Keyword Planner",
    category: "Keyword Research",
    tagline: "Free volume ranges right from Google Ads. Always a sanity check.",
    rating: 3.8,
    pricing: "Free",
    pros: ["Free", "Direct from Google", "Forecast feature"],
    cons: ["Bucketed volumes", "Requires Ads account"],
    bestFor: "Cross-checking commercial intent terms.",
    affiliate: "https://ads.google.com/home/tools/keyword-planner/",
  },
  {
    slug: "answer-the-public",
    name: "AnswerThePublic",
    category: "Keyword Research",
    tagline: "Visualizes the questions real people ask — gold for AEO content.",
    rating: 4.0,
    pricing: "Freemium",
    startsAt: "$9/mo",
    pros: ["Question discovery", "Great visuals", "Export to CSV"],
    cons: ["Free tier very limited now"],
    bestFor: "FAQ + AEO content briefs.",
    affiliate: "https://answerthepublic.com",
  },
  {
    slug: "ahrefs-rank-tracker",
    name: "Ahrefs Rank Tracker",
    category: "Rank Tracking",
    tagline: "Daily rank tracking with SERP feature + competitor visibility scoring.",
    rating: 4.7,
    pricing: "Paid",
    startsAt: "Bundled with Ahrefs",
    pros: ["Daily updates", "SERP feature tracking", "Share-of-voice"],
    cons: ["No standalone plan"],
    bestFor: "Existing Ahrefs users.",
    affiliate: "https://ahrefs.com/rank-tracker",
  },
  {
    slug: "serpwatch",
    name: "SERPWatcher by Mangools",
    category: "Rank Tracking",
    tagline: "Affordable rank tracker with a delightful UI and clear performance index.",
    rating: 4.3,
    pricing: "Paid",
    startsAt: "$29.90/mo",
    pros: ["Clean UI", "Good price", "Bundled with KWFinder"],
    cons: ["Smaller keyword DB"],
    bestFor: "Freelancers tracking up to a few sites.",
    affiliate: "https://mangools.com/?ref=freelancertamal",
    badge: "Best value",
  },
  {
    slug: "accuranker",
    name: "AccuRanker",
    category: "Rank Tracking",
    tagline: "The fastest, most accurate dedicated rank tracker. Period.",
    rating: 4.6,
    pricing: "Paid",
    startsAt: "$129/mo",
    pros: ["On-demand updates", "Local + mobile tracking", "API access"],
    cons: ["Just rank tracking"],
    bestFor: "Agencies that report rankings weekly.",
    affiliate: "https://accuranker.com",
  },
  {
    slug: "surfer-seo",
    name: "Surfer SEO",
    category: "Content & AI",
    tagline: "On-page content optimizer with NLP scoring and AI outlines.",
    rating: 4.4,
    pricing: "Paid",
    startsAt: "$89/mo",
    pros: ["Content score is genuinely useful", "Outline builder", "Audit existing pages"],
    cons: ["Can over-optimize if you trust the score blindly"],
    bestFor: "Content teams shipping weekly.",
    affiliate: "https://surferseo.com/?ref=freelancertamal",
  },
  {
    slug: "frase",
    name: "Frase",
    category: "Content & AI",
    tagline: "SERP research + AI brief generation for fast content production.",
    rating: 4.3,
    pricing: "Paid",
    startsAt: "$45/mo",
    pros: ["SERP-driven briefs", "AI writer", "Topic clusters"],
    cons: ["AI output still needs editing"],
    bestFor: "Content briefs at scale.",
    affiliate: "https://frase.io/?ref=freelancertamal",
  },
  {
    slug: "clearscope",
    name: "Clearscope",
    category: "Content & AI",
    tagline: "The premium content optimization tool top brands swear by.",
    rating: 4.6,
    pricing: "Paid",
    startsAt: "$189/mo",
    pros: ["Best-in-class content grading", "Editorial-grade reports", "Google Docs add-on"],
    cons: ["Expensive for solos"],
    bestFor: "Brands competing for high-DR SERPs.",
    affiliate: "https://www.clearscope.io",
  },
  {
    slug: "chatgpt",
    name: "ChatGPT",
    category: "Content & AI",
    tagline: "My daily driver for outlines, schema generation, and SQL/Regex.",
    rating: 4.7,
    pricing: "Freemium",
    startsAt: "$20/mo",
    pros: ["Versatile", "Code interpreter", "Custom GPTs"],
    cons: ["Hallucinates citations — verify everything"],
    bestFor: "Day-to-day SEO grunt work.",
    affiliate: "https://chat.openai.com",
  },
  {
    slug: "claude",
    name: "Claude",
    category: "Content & AI",
    tagline: "My favorite long-form writing partner — better tone control than GPT.",
    rating: 4.7,
    pricing: "Freemium",
    startsAt: "$20/mo",
    pros: ["Long context", "Excellent writing quality", "Project memory"],
    cons: ["Stricter content policies"],
    bestFor: "Editorial-grade drafts and rewrites.",
    affiliate: "https://claude.ai",
  },
  {
    slug: "majestic",
    name: "Majestic",
    category: "Link Building",
    tagline: "Trust Flow + Citation Flow — the OG metrics for link quality.",
    rating: 4.2,
    pricing: "Paid",
    startsAt: "$49.99/mo",
    pros: ["Massive historical index", "Topical Trust Flow", "Cheap entry plan"],
    cons: ["UI feels dated"],
    bestFor: "Link prospecting with topical filters.",
    affiliate: "https://majestic.com",
  },
  {
    slug: "pitchbox",
    name: "Pitchbox",
    category: "Link Building",
    tagline: "Outreach automation that doesn't feel like spam.",
    rating: 4.5,
    pricing: "Paid",
    startsAt: "$165/mo",
    pros: ["Multi-step sequences", "Native SEO data", "Team workflows"],
    cons: ["Steep price for solos"],
    bestFor: "Agencies running PR + link campaigns.",
    affiliate: "https://pitchbox.com",
  },
  {
    slug: "respona",
    name: "Respona",
    category: "Link Building",
    tagline: "Modern outreach platform with AI personalization built in.",
    rating: 4.4,
    pricing: "Paid",
    startsAt: "$99/mo",
    pros: ["AI personalization", "Strong inbox tools", "Affordable vs Pitchbox"],
    cons: ["Newer, fewer integrations"],
    bestFor: "Founder-led link building.",
    affiliate: "https://respona.com",
  },
  {
    slug: "hunter",
    name: "Hunter.io",
    category: "Link Building",
    tagline: "Find verified emails in seconds.",
    rating: 4.5,
    pricing: "Freemium",
    startsAt: "$49/mo",
    pros: ["Accurate", "Chrome extension", "Free credits monthly"],
    cons: ["Limited to email finding"],
    bestFor: "Outreach prospecting.",
    affiliate: "https://hunter.io",
  },
  {
    slug: "google-business-profile",
    name: "Google Business Profile",
    category: "Local SEO",
    tagline: "The single highest-leverage tool for local SEO. Free.",
    rating: 5.0,
    pricing: "Free",
    pros: ["Free", "Direct map pack control", "Posts + Q&A + reviews"],
    cons: ["Suspension risk if you bend the rules"],
    bestFor: "Every local business, period.",
    affiliate: "https://business.google.com",
    badge: "Free favorite",
  },
  {
    slug: "brightlocal",
    name: "BrightLocal",
    category: "Local SEO",
    tagline: "Citation tracking, local rank grids, and reputation in one tool.",
    rating: 4.5,
    pricing: "Paid",
    startsAt: "$39/mo",
    pros: ["Local rank grids", "Citation builder", "Review monitoring"],
    cons: ["Reports require setup time"],
    bestFor: "Multi-location local SEO.",
    affiliate: "https://brightlocal.com/?ref=freelancertamal",
  },
  {
    slug: "whitespark",
    name: "Whitespark",
    category: "Local SEO",
    tagline: "The citation-building OG. Local Citation Finder is still elite.",
    rating: 4.4,
    pricing: "Paid",
    startsAt: "$24/mo",
    pros: ["Best-in-class citation data", "Manual citation services", "Reputation builder"],
    cons: ["UI is utilitarian"],
    bestFor: "Citation cleanup and outreach.",
    affiliate: "https://whitespark.ca",
  },
  {
    slug: "looker-studio",
    name: "Looker Studio",
    category: "Analytics",
    tagline: "Free, flexible client reporting connected to GSC, GA4, and Sheets.",
    rating: 4.5,
    pricing: "Free",
    pros: ["Free", "Native GSC + GA4 connectors", "Embeddable dashboards"],
    cons: ["Slow on big datasets"],
    bestFor: "Client SEO dashboards.",
    affiliate: "https://lookerstudio.google.com",
    badge: "Free favorite",
  },
  {
    slug: "microsoft-clarity",
    name: "Microsoft Clarity",
    category: "Analytics",
    tagline: "Free heatmaps and session replay with no traffic limits.",
    rating: 4.6,
    pricing: "Free",
    pros: ["100% free", "Heatmaps + session replay", "GA4 integration"],
    cons: ["UI rough around the edges"],
    bestFor: "UX + CRO insights on a budget.",
    affiliate: "https://clarity.microsoft.com",
    badge: "Free favorite",
  },
  {
    slug: "profound",
    name: "Profound",
    category: "AEO / GEO",
    tagline: "Track how often your brand is cited in ChatGPT, Perplexity, and Gemini.",
    rating: 4.5,
    pricing: "Paid",
    startsAt: "Custom",
    pros: ["AI citation tracking", "Competitor visibility in LLMs", "Prompt-level insights"],
    cons: ["Enterprise pricing"],
    bestFor: "Brands serious about AEO.",
    affiliate: "https://www.tryprofound.com",
    badge: "Editor's pick",
  },
  {
    slug: "peec-ai",
    name: "Peec AI",
    category: "AEO / GEO",
    tagline: "Affordable AI search visibility tracker for SMBs and consultants.",
    rating: 4.3,
    pricing: "Paid",
    startsAt: "$89/mo",
    pros: ["Multi-engine tracking", "Affordable", "Quick setup"],
    cons: ["Newer player"],
    bestFor: "Solo SEOs auditing AEO performance.",
    affiliate: "https://peec.ai",
  },
  {
    slug: "perplexity",
    name: "Perplexity",
    category: "AEO / GEO",
    tagline: "Use it daily to see which sources answer engines actually cite.",
    rating: 4.6,
    pricing: "Freemium",
    startsAt: "$20/mo",
    pros: ["Citation-first answers", "Pro search", "API access"],
    cons: ["Citations not always perfect"],
    bestFor: "AEO SERP research.",
    affiliate: "https://www.perplexity.ai",
  },
];

export const TOP_PICKS = ["ahrefs", "screaming-frog", "google-search-console", "surfer-seo", "profound"];

export const FAQ: { q: string; a: string }[] = [
  {
    q: "Are these affiliate links?",
    a: "Some are. If you sign up through a link on this page I may earn a commission at no extra cost to you. I only recommend tools I personally use or have rigorously tested with clients.",
  },
  {
    q: "What's the absolute minimum SEO toolkit for a beginner?",
    a: "Google Search Console + Google Analytics 4 + Ahrefs Webmaster Tools + Screaming Frog (free up to 500 URLs). That's a $0 stack that covers 80% of what you need to start.",
  },
  {
    q: "Ahrefs vs Semrush — which one should I buy?",
    a: "If you mostly do SEO and link analysis, Ahrefs. If you need SEO + paid + competitor research in one place, Semrush. Both are excellent — try the trials before committing.",
  },
  {
    q: "Do I need a paid SEO tool to rank?",
    a: "No. You can rank with Search Console, Keyword Planner, and a free crawler. But paid tools save dozens of hours per month once you're scaling content or managing client sites.",
  },
];